import React from 'react';
import { createPortal } from 'react-dom';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import type {
  BroadcastSegment,
  BroadcastMessageType,
  BroadcastDeliveryChannel,
} from 'types/interfaces/broadcast';
import './ConfirmBroadcastModal.css';

const MESSAGE_TYPE_LABELS: Record<BroadcastMessageType, string> = {
  normal: 'Normal email',
  blog_announcement: 'Blog Announcement',
};

const DELIVERY_LABELS: Record<BroadcastDeliveryChannel, string> = {
  email: 'Email only',
  in_app: 'In-app only',
  both: 'Email + in-app',
};

const SEGMENT_LABELS: Record<Exclude<BroadcastSegment, 'specific'>, string> = {
  all: 'All Users',
  elite: 'Elite Users',
  free: 'Free Users',
};

const resolveAudienceLabel = (
  segment: BroadcastSegment,
  messageType: BroadcastMessageType,
  targetUsername?: string,
): string => {
  if (segment === 'specific') {
    return targetUsername ? `Specific User: @${targetUsername}` : 'Specific User';
  }
  const base = SEGMENT_LABELS[segment];
  return messageType === 'blog_announcement'
    ? `${base} (with New Features opt-in)`
    : base;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  recipientCount: number;
  emailCount: number;
  segment: BroadcastSegment;
  messageType: BroadcastMessageType;
  deliveryChannel: BroadcastDeliveryChannel;
  subject: string;
  targetUsername?: string;
}

const ConfirmBroadcastModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  recipientCount,
  emailCount,
  segment,
  messageType,
  deliveryChannel,
  subject,
  targetUsername,
}) => {
  if (!isOpen) return null;

  const audienceLabel = resolveAudienceLabel(segment, messageType, targetUsername);
  const willEmail = deliveryChannel === 'email' || deliveryChannel === 'both';
  const willInApp = deliveryChannel === 'in_app' || deliveryChannel === 'both';

  const modalContent = (
    <div className="confirm-broadcast-modal-overlay" onClick={isLoading ? undefined : onClose}>
      <div className="confirm-broadcast-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-broadcast-modal-header">
          <h2 className="confirm-broadcast-modal-title">Send Broadcast Message?</h2>
          {!isLoading && (
            <button className="confirm-broadcast-close-button" onClick={onClose}>
              <img
                src={AssetUrls.icons.dropdowns.close}
                alt="Close"
                style={{ width: 16, height: 16 }}
              />
            </button>
          )}
        </div>

        <div className="confirm-broadcast-modal-content">
          <div className="confirm-summary">
            <div className="confirm-summary-row">
              <span className="confirm-summary-key">Type:</span>
              <span className="confirm-summary-value">{MESSAGE_TYPE_LABELS[messageType]}</span>
            </div>
            <div className="confirm-summary-row">
              <span className="confirm-summary-key">Audience:</span>
              <span className="confirm-summary-value">{audienceLabel}</span>
            </div>
            <div className="confirm-summary-row">
              <span className="confirm-summary-key">Delivery:</span>
              <span className="confirm-summary-value">{DELIVERY_LABELS[deliveryChannel]}</span>
            </div>
            <div className="confirm-summary-row">
              <span className="confirm-summary-key">Subject:</span>
              <span className="confirm-summary-value">{subject}</span>
            </div>
          </div>

          <p className="confirm-question">
            You are about to send to <strong>{recipientCount.toLocaleString()}</strong> recipient{recipientCount === 1 ? '' : 's'}.
          </p>

          {willEmail && (
            <p className="confirm-question">
              <strong>{emailCount.toLocaleString()}</strong> will receive an email.
            </p>
          )}

          {willInApp && (
            <p className="confirm-question">
              <strong>{recipientCount.toLocaleString()}</strong> will receive an in-app message.
            </p>
          )}

          <div className="warning-box">
            <strong>Warning:</strong> This action cannot be undone. Messages will be delivered immediately to all recipients.
          </div>

          {isLoading && (
            <p className="info-text">
              Sending broadcast... This may take up to 30 seconds for large segments.
            </p>
          )}
        </div>

        <div className="confirm-broadcast-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? "Sending..." : `Send to ${recipientCount.toLocaleString()} ${recipientCount === 1 ? 'User' : 'Users'}`}
            color="purple"
            onPress={onConfirm}
            isLoading={isLoading}
            isDisabled={isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ConfirmBroadcastModal;
